import { z } from 'zod'
import { oc } from '@orpc/contract'

// Document-type coding stamped on a saved letter. Shares the local
// document-type system used by the other notes we author, so the letter shows
// up in the chart's Documents section like any other patient document.
export const PATIENT_LETTER_DOCUMENT_TYPE_SYSTEM = 'https://behuman.health/document-type'
export const PATIENT_LETTER_DOCUMENT_TYPE_CODE = 'PatientLetter'

// Which template the letter was produced from, recorded as a DocumentReference
// category so the source template stays identifiable after the fact.
export const PATIENT_LETTER_TEMPLATE_SYSTEM = 'https://behuman.health/letter-template'

// Stamped on a letter filed for a signature request, so refiling the same request
// finds the document that already exists instead of creating a second one.
export const PATIENT_LETTER_REQUEST_SYSTEM = 'https://behuman.health/letter-request'

export const PATIENT_LETTER_EMAIL_SKIP_CODES = [
  'no_email',
  'email_send_failed',
  'email_not_requested',
  'already_filed',
] as const

// Save a practitioner-signed letter to the patient chart, and optionally send
// the patient the practitioner-drafted notification email. The letter PDF is
// rendered in the browser (same pdfmake letterhead as the other beHuman
// documents) and arrives here as base64 — this handler only persists it and
// notifies. The notification deliberately carries no clinical content: it tells
// the patient a new document is waiting in the portal, matching the patient
// instructions flow.
export const createPatientLetter = oc
  .route({ method: 'POST', path: '/v2/patients/{patientId}/letters' })
  .input(
    z.object({
      patientId: z.string(),
      templateId: z.string(),
      // Letter title, e.g. "Work Absence Letter". Used as the document
      // description and as the type display in the Documents table.
      title: z.string(),
      pdfBase64: z.string(),
      fileName: z.string(),
      // Omit to save the letter without notifying the patient.
      email: z
        .object({
          subject: z.string(),
          body: z.string(),
        })
        .optional(),
    }),
  )
  .output(
    z.object({
      documentReferenceId: z.string(),
      emailSent: z.boolean(),
      emailSkipReason: z.string().optional(),
      emailSkipCode: z.enum(PATIENT_LETTER_EMAIL_SKIP_CODES).optional(),
    }),
  )
